<template>
  <div class="mb-10">
    <v-breadcrumbs class="px-0" :items="breadcrumbs"></v-breadcrumbs>

    <div v-if="isNew" class="d-flex">
      <h2>延長保育日誌 新規作成</h2>
    </div>

    <div v-else class="d-flex">
      <h2>{{ date }}</h2>
      <v-spacer />
      <div v-if="isApplied && (isDirector || isChief)" class="d-flex">
        <v-btn
          @click="updateApprovalStatus(true)"
          :disabled="
            (isChief && isApprovedByChief) ||
            (isDirector && isApprovedByDirector)
          "
          :color="(isChief && isApprovedByChief) || (isDirector && isApprovedByDirector) ? 'grey' : 'primary'"
          class="mr-4"
          variant="flat"
          size="large"
          >承認</v-btn
        >
        <v-btn
          v-if="
            (isChief && isApprovedByChief) ||
            (isDirector && isApprovedByDirector)
          "
          @click="updateApprovalStatus(false)"
          color="error"
          variant="outlined"
          size="large"
          >承認取り消し</v-btn
        >
      </div>
    </div>

    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="primary"
    ></v-progress-linear>

    <v-row v-if="!isNew" class="mt-8">
      <v-spacer />
      <v-col cols="2">
        <v-text-field
          label="施設長"
          readonly
          variant="outlined"
          :model-value="directorApproveStamp"
          color="primary"
        >
        </v-text-field>
      </v-col>
      <v-col cols="2">
        <v-text-field 
          label="主任" 
          readonly 
          variant="outlined" 
          :model-value="cheifApproveStamp"
          color="primary"
        >
        </v-text-field>
      </v-col>
      <v-col cols="2">
        <v-select
          :items="staffItems"
          label="担当者"
          multiple
          item-title="name"
          item-value="userId"
          variant="outlined"
          clearable
          v-model="inputValue.staff"
          color="primary"
        ></v-select>
      </v-col>
      <v-col cols="2">
        <v-select
          :items="staffItems"
          label="申請者"
          item-title="name"
          item-value="userId"
          variant="outlined"
          clearable
          v-model="inputValue.appliedBy"
          color="primary"
        ></v-select>
      </v-col>
    </v-row>

    <v-container v-if="!isNew">
      <v-row v-if="!isNew" no-gutters>
        <v-col cols="2">
          <h4>園児人数</h4>
          <div style="font-size: 36px; font-weight: bold">
            {{ diary ? diary.totalChildrenCount : "" }}
          </div>
        </v-col>
        <v-col cols="2">
          <h4>月極</h4>
          <div style="font-size: 36px; font-weight: bold">
            {{ monthlyChildrenCount }}
          </div>
        </v-col>
        <v-col cols="2">
          <h4>スポット</h4>
          <div style="font-size: 36px; font-weight: bold">
            {{ spotChildrenCount }}
          </div>
        </v-col>
        <v-spacer />
      </v-row>
    </v-container>

    <v-row v-if="isNew" class="d-flex mt-8">
      <v-col cols="3"> </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="watchDate"
          label="日付"
          class="mx-2"
          type="date"
          variant="outlined"
          density="compact"
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-select
          :items="staffItems"
          label="担当者"
          multiple
          item-title="name"
          item-value="userId"
          variant="outlined"
          clearable
          density="compact"
          v-model="inputValue.staff"
        ></v-select>
      </v-col>
      <v-col cols="3">
        <v-select
          :items="staffItems"
          label="申請者"
          item-title="name"
          item-value="userId"
          density="compact"
          variant="outlined"
          clearable
          v-model="inputValue.appliedBy"
        ></v-select>
      </v-col>
    </v-row>

    <v-container class="pt-8">
      <v-row>
        <v-col cols="6">
          <h3>活動内容</h3>
          <v-textarea v-model="inputValue.activity" variant="outlined" color="primary"></v-textarea>
        </v-col>
        <v-col cols="6">
          <h3>引き継ぎ・伝達事項</h3>
          <v-textarea v-model="inputValue.message" variant="outlined" color="primary"></v-textarea>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <h3>備考</h3>
          <v-textarea v-model="inputValue.remarks" variant="outlined" color="primary"></v-textarea>
        </v-col>
      </v-row>
      <v-row v-if="!isNew && diary" class="py-4">
        <v-col cols="8">
          <v-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">園児氏名</th>
                  <th class="text-left">利用区分</th>
                  <th class="text-left">降園時刻</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(childItem, i) in diary.children" :key="i">
                  <td>{{ childItem.name }}</td>
                  <td>{{ getCategoryName(childItem.category) }}</td>
                  <td>{{ formatDate(childItem.leftAt) }}</td>
                </tr>
              </tbody>
            </template>
          </v-table>
        </v-col>
      </v-row>
    </v-container>

    <v-row v-if="isNew" justify="center" class="mt-8">
      <v-btn
        @click="createDiary(true)"
        class="mx-2"
        color="primary"
        variant="outlined"
        size="large"
        >下書きとして作成</v-btn
      >

      <v-btn
        @click="createDiary(false)"
        :disabled="!inputValue.appliedBy"
        class="mx-2"
        :color="!inputValue.appliedBy ? 'grey' : 'primary'"
        size="large"
        variant="flat"
        >作成して申請</v-btn
      >
    </v-row>

    <v-row v-else justify="center" class="mt-8">
      <v-btn
        @click="updateDiary(false)"
        class="mx-2"
        color="primary"
        variant="outlined"
        size="large"
      >
        <div v-if="isDraft">下書きとして保存</div>
        <div v-else>差戻し</div>
      </v-btn>

      <v-btn
        @click="updateDiary(true)"
        :disabled="inputValue.status === 'applied'"
        :color="inputValue.status === 'applied' ? 'grey' : 'primary'"
        class="mx-2"
        variant="flat"
        size="large"
        >申請</v-btn
      >
    </v-row>
  </div>
</template>

<script lang="ts">
import LocalDataService from "@/service/LocalDataService";
import { meModule } from "@/store/dataModules/meModule";
import { nurseryHoursDiaryModule } from "@/store/dataModules/nurseryHoursDiaryModule";
import { nurseryUserModule } from "@/store/dataModules/nurseryUserModule";
import { NurseryHoursDiaryDetailResponse } from "chaild-api/lib";
import dayjs from "dayjs";
import { defineComponent } from "vue";

export default defineComponent({
  name: "NurseryHoursDiaryEdit",

  async mounted() {
    if (!this.isNew) {
      await this.getDiary();
      await nurseryUserModule.listStaffs({
        date: this.date,
        diaryId: this.diaryId,
        diaryType: 'hours'
      });
    } else {
      this.watchDate = dayjs().format("YYYY-MM-DD");
      this.inputValue.date = dayjs().format("YYYY-MM-DD");
      await nurseryUserModule.listStaffs({ date: this.date })
    }
  },

  data() {
    return {
      isLoading: false,
      inputValue: {
        date: "",
        activity: "",
        remarks: "",
        message: "",
        status: "draft" as "draft" | "applied",
        appliedBy: undefined as undefined | number,
        staff: [] as number[],
      },
      watchDate: "",
    };
  },

  watch: {
    watchDate: {
      handler() {
        this.inputValue.date = this.watchDate;
        nurseryUserModule.listStaffs({ date: this.watchDate });
      },
    },
  },

  computed: {
    diaryId: function (): any {
      return this.$route.query.diaryId;
    },
    date: function (): any {
      return this.$route.query.date;
    },
    isNew: function (): boolean {
      return this.diaryId && this.date ? false : true;
    },
    diary: function (): NurseryHoursDiaryDetailResponse | null {
      return nurseryHoursDiaryModule.diary;
    },
    monthlyChildrenCount: function (): string {
      if (this.diary) {
        const children = this.diary.children.filter(
          (child) => (child.category as any) == "monthly"
        );
        return String(children.length);
      } else {
        return "";
      }
    },
    spotChildrenCount: function (): string {
      if (this.diary) {
        const children = this.diary.children.filter(
          (child) => (child.category as any) == "spot"
        );
        return String(children.length);
      } else {
        return "";
      }
    },
    breadcrumbs: function () {
      return [
        {
          title: "延長保育日誌",
          disabled: false,
          href: "/nursery-hours-diary/list",
          color: "primary"
        },
        {
          title: "",
        },
      ];
    },
    staffItems: function () {
      return nurseryUserModule.staffs.filter(s => !s.isHidden).map((staff) => {
        return {
          userId: staff.userId,
          name: `${staff.lastName} ${staff.firstName}`,
        };
      });
    },
    cheifApproveStamp(): string {
      if (this.diary) {
        if (this.diary.approvedBy.chief && this.diary.approvedBy.chief != -1) {
          return `${this.diary.approvedBy.chief.lastName} ${this.diary.approvedBy.chief.firstName}`;
        }
      }
      return "-";
    },
    directorApproveStamp(): string {
      if (this.diary) {
        if (
          this.diary.approvedBy.director &&
          this.diary.approvedBy.director != -1
        ) {
          return `${this.diary.approvedBy.director.lastName} ${this.diary.approvedBy.director.firstName}`;
        }
      }
      return "-";
    },
    me() {
      return meModule.me;
    },
    isDirector() {
      if (this.me) {
        const nurseryId = LocalDataService.getNurseryId();
        const currentAccount = this.me.nurserySchools.find(
          (s) => s.nurseryId === nurseryId
        );
        if (currentAccount && currentAccount.isDirector) {
          return true;
        }
      }
      return false;
    },
    isChief() {
      if (this.me) {
        const nurseryId = LocalDataService.getNurseryId();
        const currentAccount = this.me.nurserySchools.find(
          (s) => s.nurseryId === nurseryId
        );
        if (currentAccount && currentAccount.isChief) {
          return true;
        }
      }
      return false;
    },
    isApprovedByDirector(): boolean {
      if (this.diary) {
        return (
          this.diary.approvedBy.director != null &&
          this.diary.approvedBy.director != -1
        );
      }
      return false;
    },
    isApprovedByChief(): boolean {
      if (this.diary) {
        return (
          this.diary.approvedBy.chief != null &&
          this.diary.approvedBy.chief != -1
        );
      }
      return false;
    },
    isApplied(): boolean {
      if (this.diary) {
        return this.diary.status === "applied";
      }
      return false;
    },
    isDraft(): boolean {
      if (this.diary) {
        return this.diary.status === "draft";
      }
      return false;
    },
  },

  methods: {
    async createDiary(isDraft?: boolean) {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        if (isDraft) {
          this.inputValue.status = "draft";
        } else {
          this.inputValue.status = "applied";
        }

        const response = await nurseryHoursDiaryModule
          .createDiary({
            nurseryId: nurseryId,
            ...this.inputValue,
          })
          .catch(() => (this.isLoading = false));
        if (response) {
          this.$router.push({ path: "/nursery-hours-diary/list" });
        }
      }
      this.isLoading = false;
    },
    async getDiary() {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && this.date) {
        const response = await nurseryHoursDiaryModule.getDiary({
          nurseryId: String(nurseryId),
          date: this.date,
        });
        if (response) {
          this.inputValue = {
            date: this.date,
            activity: (response as any).activity || "",
            remarks: (response as any).remarks || "",
            message: (response as any).message || "",
            status: (response as any).status,
            
            appliedBy: response.createdBy ? response.createdBy.userNurseryId : undefined,
            
            staff:
              response.staff.length != 0
                ? response.staff.map((s) => {
                    return s.userNurseryId;
                  })
                : [],
          };
        }
      }
      this.isLoading = false;
    },
    async updateDiary(isApply?: boolean) {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && this.diaryId) {
        // apply
        if (isApply) {
          this.inputValue.status = "applied";
        } else {
          this.inputValue.status = "draft";
        }
        const response = await nurseryHoursDiaryModule.updateDiary({
          nurseryId: nurseryId,
          nurseryHoursDiaryId: this.diaryId,
          ...this.inputValue,
        });
        if (response) {
          this.$router.push({ path: "/nursery-hours-diary/list" });
        }
      }
      this.isLoading = false;
    },
    async updateApprovalStatus(isApprove?: boolean) {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (
        nurseryId &&
        this.diaryId &&
        isApprove != undefined &&
        isApprove != null
      ) {
        const response = await nurseryHoursDiaryModule.updateApprovalStatus({
          nurseryId,
          nurseryHoursDiaryId: this.diaryId,
          approvalStatus: isApprove ? "approved" : "rejected",
        });
        if (response) {
          this.$router.push({ path: "/nursery-hours-diary/list" });
        }
      }
      this.isLoading = false;
    },
    formatDate(date: string): string {
      if (date) {
        return dayjs(date).format("HH:mm");
      }
      return "";
    },
    getCategoryName(category: string | null): string {
      if (category == "monthly") {
        return "月極";
      } else if (category == "spot") {
        return "スポット";
      } else {
        return "-";
      }
    },
  },
});
</script>